import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { entryAPI } from '@/lib/api';
import { 
  FileText, Users, Calculator, Eye, Building, Clock,
  Plus, ChevronRight, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';

export default function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEntries: 0,
    recentEntries: []
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await entryAPI.getAll();
      const data = response.data || [];
      setEntries(data);
      setStats({
        totalEntries: data.length,
        recentEntries: data.slice(0, 5)
      });
    } catch (error) {
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { icon: FileText, label: 'Build Blueprint', path: '/dashboard/blueprint', color: 'bg-blue-500' },
    { icon: Users, label: 'Add Students', path: '/dashboard/students', color: 'bg-emerald-500' },
    { icon: Calculator, label: 'Enter Marks', path: '/dashboard/marks', color: 'bg-amber-500' },
    { icon: Eye, label: 'Generate Report', path: '/dashboard/preview', color: 'bg-purple-500' },
  ];

  return (
    <div className="p-6 lg:p-8">
      {/* Welcome header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy">
          Welcome back!
        </h1>
        <p className="text-slate-600 mt-1">
          {user?.school_name} • {user?.district}
        </p>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {quickActions.map((action) => (
          <button
            key={action.path}
            onClick={() => navigate(action.path)}
            className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all text-left group"
            data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center mb-4`}>
              <action.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-navy">{action.label}</h3>
            <ChevronRight className="w-4 h-4 text-slate-400 mt-2 group-hover:translate-x-1 transition-transform" />
          </button>
        ))}
      </div>

      {/* Stats cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-navy">{stats.totalEntries}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">School</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold text-navy truncate">{user?.school_name}</div>
            <div className="text-sm text-slate-500">{user?.udise_code}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Location</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold text-navy">{user?.block}</div>
            <div className="text-sm text-slate-500">{user?.cluster} Cluster</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent entries */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Recent Reports</CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/dashboard/blueprint')}
          >
            <Plus className="w-4 h-4 mr-2" />
            New Report
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : stats.recentEntries.length > 0 ? (
            <div className="space-y-3">
              {stats.recentEntries.map((entry) => (
                <div 
                  key={entry.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/dashboard/preview?entry=${entry.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-navy" />
                    </div>
                    <div>
                      <p className="font-medium text-navy">
                        Class {entry.class_name} - {entry.subject}
                      </p>
                      <p className="text-sm text-slate-500">
                        {entry.exam} • {entry.year}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Clock className="w-4 h-4" />
                    {new Date(entry.updated_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-700 mb-2">No reports yet</h3>
              <p className="text-sm text-slate-500 mb-4">
                Create your first Format-A report by building a blueprint
              </p>
              <Button onClick={() => navigate('/dashboard/blueprint')}>
                <Plus className="w-4 h-4 mr-2" />
                Create Blueprint
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
